import React, { useEffect, useState } from "react";
import { Accordion, Col, ListGroup, Row, Spinner } from "react-bootstrap";
import ReactCountryFlag from "react-country-flag";
import { useLocation } from "react-router-dom";
import { Bar, BarChart, Cell, Legend, Pie, PieChart, Tooltip, XAxis } from "recharts";

import DstButton from "../../components/DstButton/DstButton";
import MultiSelectLiveDropdown from "../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import { useSession } from "../../contexts/SessionContext";
import translate from "../../services/Translate";
import { normalizeString } from "../../services/UtilityService";
import { CohortSurveyAnswer } from "./InsightsCohortSurvey.d";
import {
  COLOR_PALETTE,
  calculateGlobalNote,
  fetchCohortSurveysAnswers,
  getChartData,
} from "./InsightsCohortSurvey.function";
import "./InsightsCohortSurvey.scss";

const STICKY_TOP = 67.5;

const InsightsCohortSurvey = () => {
  const { language } = useSession();
  const [loading, setLoading] = useState<boolean>(true);
  const [surveyAnswer, setSurveyAnswer] = useState<CohortSurveyAnswer>();
  const [surveyChart, setSurveyChart] = useState<string>("PieChart");
  const { state } = useLocation() as any;
  const [selectedCohortId, setSelectedCohortId] = useState<number | null>(state?.cohortId ? +state.cohortId : null);

  const MAX_LEGEND_ITEMS = 10;
  const MAX_LEGEND_LABEL_LENGTH = 50;
  const MAX_LEGEND_TOTAL_LABEL_LENGTH = 100;

  useEffect(() => {
    if (selectedCohortId) {
      fetchCohortSurveysAnswers(selectedCohortId, language, setSurveyAnswer, setLoading);
    }
  }, [language, selectedCohortId]);

  const _renderCustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;

    const { percentage, response } = payload[0].payload;

    return (
      <div className="m-regular p-3 white-background border rounded-2 shadow-sm">
        {response || translate(language, "PAGES.INSIGHTS.COHORT_SURVEY.NO_RESPONSES")} : {percentage}%
      </div>
    );
  };

  const _renderCustomLegend = (data: any[]) => {
    if (!data?.length) return null;

    const totalLength = data.reduce((sum, item) => sum + String(item.response).length, 0);

    const shouldShowLegend =
      data.length <= MAX_LEGEND_ITEMS &&
      totalLength <= MAX_LEGEND_TOTAL_LABEL_LENGTH &&
      data.every((item) => String(item.response).length <= MAX_LEGEND_LABEL_LENGTH);

    if (!shouldShowLegend) return null;

    return (
      <div className="text-center">
        <div className="d-flex justify-content-around flex-wrap gap-2 fw-semibold">
          {data.every((answer) => !isNaN(answer.response))
            ? data
                .sort((a, b) => a.response - b.response)
                .map((item, index) => (
                  <div key={`legend-item-${index}`} style={{ color: item.color }}>
                    {item.response}
                  </div>
                ))
            : data.map((item, index) => (
                <div
                  key={`legend-item-${index}`}
                  style={{
                    color: COLOR_PALETTE[index % COLOR_PALETTE.length],
                  }}
                >
                  {item.response}
                </div>
              ))}
        </div>
        {data.every((answer) => !isNaN(answer.response)) && (
          <p className="m-regular mt-3 mb-0 text-muted">
            {translate(language, "PAGES.INSIGHTS.COHORT_SURVEY.GLOBAL_SCORE")} {calculateGlobalNote(data)}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="p-md-5 p-3" id="insights-cohort-survey-page">
      <div className="position-sticky top-0 white-background pt-3 pb-2 mb-3 border-bottom" style={{ zIndex: 2 }}>
        <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
          <h1 className="h3 mb-2 mb-sm-0">{translate(language, "PAGES.INSIGHTS.COHORT_SURVEY.TITLE")}</h1>
          <div className="d-flex align-items-center gap-2">
            <MultiSelectLiveDropdown
              category={["cohorts"]}
              label={
                selectedCohortId
                  ? `${translate(
                      language,
                      "PAGES.INSIGHTS.COHORT_SURVEY.COHORT_SELECTION.COHORT_SELECTED"
                    )} ${selectedCohortId}`
                  : translate(language, "PAGES.INSIGHTS.COHORT_SURVEY.COHORT_SELECTION.SELECT_COHORT")
              }
              language={language}
              mockClass="rounded-0 rounded-end"
              toggleClass="min-w-100px m-regular"
              onConfirm={(selectedCohort: any) => setSelectedCohortId(selectedCohort[0]?.value.id)}
              isSingleSelect
              align="end"
            />
            <DstButton
              btnClass="dst-icon-switch-horizontal-01"
              variant="secondary"
              clickFunction={() => setSurveyChart(surveyChart === "PieChart" ? "BarChart" : "PieChart")}
              tooltip={surveyChart === "PieChart" ? "Bar" : "Pie"}
              value=""
            />
          </div>
        </div>
      </div>
      <div>
        {selectedCohortId ? (
          loading ? (
            <Spinner />
          ) : (
            <div>
              {!!surveyAnswer?.count ? (
                Object.keys(surveyAnswer?.answers).map((surveyLanguage: string, languageIndex: number) => (
                  <div key={`language-${languageIndex}`} className="mb-4">
                    {Object.keys(surveyAnswer?.answers[surveyLanguage]).map(
                      (surveyType: string, surveyTypeIndex: number) => (
                        <div key={`language-${languageIndex}-${surveyTypeIndex}`}>
                          {Object.keys(surveyAnswer?.answers[surveyLanguage][surveyType]).map(
                            (surveyTypeQuestionName: string, surveyTypeQuestionIndex: number) => (
                              <Accordion
                                key={`survey-type-${languageIndex}-${surveyTypeQuestionName}`}
                                className="mb-4"
                              >
                                <Accordion.Item eventKey="0">
                                  <div className="position-sticky" style={{ top: STICKY_TOP - 8, zIndex: 1 }}>
                                    <Accordion.Header>
                                      <div className="d-flex align-items-center">
                                        <ReactCountryFlag
                                          svg
                                          countryCode={surveyLanguage.split("-")[1]}
                                          title={surveyLanguage}
                                          className="me-2"
                                        />
                                        <div className="fw-semibold">
                                          {normalizeString(surveyType)}{" "}
                                          <span className="smoke-gray-font">
                                            / {normalizeString(surveyTypeQuestionName)}
                                          </span>
                                        </div>
                                      </div>
                                    </Accordion.Header>
                                  </div>
                                  <Accordion.Body className="pt-3">
                                    <div>
                                      {surveyAnswer?.answers[surveyLanguage][surveyType][surveyTypeQuestionName]
                                        .filter((elem: any) => !!elem)
                                        .map(
                                          (
                                            surveyQuestionAnswers: Array<any>,
                                            surveyQuestionIndex: number,
                                            arr: Array<any>
                                          ) => (
                                            <div
                                              key={`survey-question-${languageIndex}-${surveyTypeQuestionIndex}-${surveyQuestionIndex}`}
                                              className="pb-3 mb-3"
                                            >
                                              <h4 className="h3 mb-3">{surveyQuestionAnswers[0].content.question}</h4>
                                              <Row>
                                                <Col lg={6} className="mb-3 mb-md-0">
                                                  <p className="m-regular text-muted mb-3">
                                                    {translate(
                                                      language,
                                                      "PAGES.INSIGHTS.COHORT_SURVEY.TOTAL_RESPONSES"
                                                    ).replace("{{COUNT}}", String(surveyQuestionAnswers.length))}
                                                  </p>
                                                  <div className="d-flex align-items-start gap-3">
                                                    {surveyChart === "PieChart" ? (
                                                      <PieChart width={400} height={300}>
                                                        <Pie
                                                          data={getChartData(surveyQuestionAnswers)}
                                                          cx={200}
                                                          cy={100}
                                                          labelLine={false}
                                                          label={false}
                                                          outerRadius={80}
                                                          fill="#8884d8"
                                                          dataKey="count"
                                                        >
                                                          {getChartData(surveyQuestionAnswers).map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                                          ))}
                                                        </Pie>
                                                        <Tooltip content={_renderCustomTooltip} />
                                                        <Legend
                                                          content={() =>
                                                            _renderCustomLegend(getChartData(surveyQuestionAnswers))
                                                          }
                                                        />
                                                      </PieChart>
                                                    ) : (
                                                      <div>
                                                        <BarChart
                                                          width={400}
                                                          height={300}
                                                          data={getChartData(surveyQuestionAnswers)}
                                                        >
                                                          <XAxis
                                                            dataKey="response"
                                                            tickLine={false}
                                                            axisLine={false}
                                                            hide
                                                          />
                                                          <Tooltip content={_renderCustomTooltip} />
                                                          <Legend
                                                            content={() =>
                                                              _renderCustomLegend(getChartData(surveyQuestionAnswers))
                                                            }
                                                          />

                                                          <Bar dataKey="count" fill="#8884d8">
                                                            {getChartData(surveyQuestionAnswers).map((entry, index) => (
                                                              <Cell key={`cell-${index}`} fill={entry.color} />
                                                            ))}
                                                          </Bar>
                                                        </BarChart>
                                                      </div>
                                                    )}
                                                  </div>
                                                </Col>
                                                <Col lg={6}>
                                                  <h3 className="h6 mb-2">
                                                    {translate(language, "PAGES.INSIGHTS.COHORT_SURVEY.RESPONSES")}
                                                  </h3>
                                                  <ListGroup className="text-break">
                                                    {Object.entries(
                                                      surveyQuestionAnswers.reduce(
                                                        (acc: { [key: string]: number }, answer) => {
                                                          const response =
                                                            answer.content.answer ||
                                                            translate(
                                                              language,
                                                              "PAGES.INSIGHTS.COHORT_SURVEY.NO_RESPONSES"
                                                            );
                                                          acc[response] = (acc[response] || 0) + 1;
                                                          return acc;
                                                        },
                                                        {} as { [key: string]: number }
                                                      )
                                                    )
                                                      .sort((a, b) => b[1] - a[1])
                                                      .map(([response, count], index) => (
                                                        <ListGroup.Item key={`response-${index}`} className="py-2">
                                                          {count > 1 ? (
                                                            <React.Fragment>
                                                              {response}{" "}
                                                              <span style={{ color: "var(--color-gray)" }}>
                                                                ({count})
                                                              </span>
                                                            </React.Fragment>
                                                          ) : (
                                                            response
                                                          )}
                                                        </ListGroup.Item>
                                                      ))}
                                                  </ListGroup>
                                                </Col>
                                              </Row>
                                              <hr
                                                className={`my-5 mx-auto ${
                                                  surveyQuestionIndex === arr.length - 1 ? "d-none" : ""
                                                }`}
                                                style={{ width: "50%" }}
                                              />
                                            </div>
                                          )
                                        )}
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                              </Accordion>
                            )
                          )}
                        </div>
                      )
                    )}
                  </div>
                ))
              ) : (
                <p className="text-muted">{translate(language, "PAGES.INSIGHTS.COHORT_SURVEY.NO_RESPONSES")}</p>
              )}
            </div>
          )
        ) : (
          <p className="text-muted">{translate(language, "PAGES.INSIGHTS.COHORT_SURVEY.NO_COHORT_SELECTED")}</p>
        )}
      </div>
    </div>
  );
};

export default InsightsCohortSurvey;
